import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectDocumentSummary } from '../../../Constants';
import { IAction, SlicePropsWithData } from '../../store';
import { IDocDefaultFields } from './sliceProjectDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

/** Session storage key for the IdDocIndexes of the user's selected documents. */
const StorageKeySelectedDocs = 'selectedDocs';

/**
 * Initial slice state value.
 */
const initialState: IProjectSelectedDocumentsSlice = {
    storage: JSON.parse(sessionStorage.getItem(StorageKeySelectedDocs) ?? '{}'),
    data: null,
    loading: false,
    error: null,
    maxAllowedToSelect: 200,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Updates the collection of selected documents within the project.
     * @param state - Current slice state object.
     * @param action - Action containing the new list of selected documents.
     */
    updateSelectedDocuments(
        state: IProjectSelectedDocumentsSlice,
        action: IAction<ISelectedDocuments>,
    ) {
        sessionStorage.setItem(StorageKeySelectedDocs, JSON.stringify(action.payload));
        state.storage = action.payload;
    },

    updateProjectDocumentSummary(
        state: IProjectSelectedDocumentsSlice,
        action: IAction<ISelectedDocumentSummary>,
    ) {
        state.data = action.payload;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches summary data on the selected documents in a project.
     * @param config - Object that contains the project database's replicaId, list of document Ids, the view to search, and the criteria by which to sort them.
     * @returns Promise for the HTTP request.
     */
    getProjectDocumentSummary: async (config: {
        replicaId: string;
        documentIds: string[];
        viewName: string;
        columnName: string;
        fields: string[];
    }): Promise<ISelectedDocumentSummary> => {
        try {
            const { replicaId, viewName, documentIds, columnName, fields } = config;

            const request: RequestInit = {
                headers: {
                    fieldset: fields.join(';'),
                    universalIds: documentIds.join(';'),
                    viewName,
                    columnName,
                },
            };

            const url = urlProjectDocumentSummary.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectSelectedDocumentsSlice>) => {
        /**
         * Reducers for the 'getProjectDocumentSummary' thunk.
         */

        builder.addCase(
            getProjectDocumentSummary.pending,
            (state: Draft<IProjectSelectedDocumentsSlice>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentSummary.fulfilled,
            (
                state: Draft<IProjectSelectedDocumentsSlice>,
                action: IAction<ISelectedDocumentSummary>,
            ) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectDocumentSummary.rejected,
            (state: Draft<IProjectSelectedDocumentsSlice>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocumentSummary = createAsyncThunk(
    'project/documentSummary',
    thunks.getProjectDocumentSummary,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectSelectedDocumentsSlice
    extends SlicePropsWithData<ISelectedDocumentSummary> {
    /** Base information on the selected documents in the project. */
    storage: ISelectedDocuments;

    /** Max number of documents that is allowed to select for downloading */
    maxAllowedToSelect: number;
}

/**
 * Interface that describes the object that is stored in SessionStorage pertaining to the documents that are selected in the project.
 */
export interface ISelectedDocuments {
    /** Project replicaId. */
    replicaId: string;

    /** Project area id. */
    areaName: string;

    /** Array of selected document ids.  */
    documentIds: string[];
}

/**
 * Interface that describes the Object that is returned from a document summary request.
 */
export interface ISelectedDocumentSummary {
    /** Metadata of documents that were successfully queried. */
    documents: IDocDefaultFields[];

    /** Ids of documents that were not successfully queried. */
    errors: string[];
}

/**
 * Create the redux slice.
 */
export const projectSelectedDocumentsSlice = createSlice({
    name: 'projectSelectedDocuments',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { updateSelectedDocuments, updateProjectDocumentSummary } =
    projectSelectedDocumentsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectSelectedDocumentsSlice.reducer;
